import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Link, SpaceBetween } from '@amzn/awsui-components-core';
import styles from './styles/EmbedApp.module.scss';
import { LoadingSpinner } from 'src/components/generic-components/LoadingSpinner';
import { useQuickSightAuth } from 'src/hooks/useQuickSightAuth';
import { logger } from 'src/logger';

interface QuickSightDashboardComponentProps {
  src: string;
  title: string;
}

const getQuickSightSignInUrl = (dashboardUrl: string): string => {
  const match = dashboardUrl.match(/^(https:\/\/[^/]+\/sn\/account\/[^/]+)/);
  return match ? `${match[1]}/start/home` : dashboardUrl;
};

const getQuickSightAccountName = (dashboardUrl: string): string => {
  const match = dashboardUrl.match(/\/sn\/account\/([^/]+)/);
  return match ? match[1] : 'QuickSight';
};

const QuickSightDashboardComponent: React.FC<QuickSightDashboardComponentProps> = ({ src, title }) => {
  const authState = useQuickSightAuth(true);
  const signInUrl = useMemo(() => getQuickSightSignInUrl(src), [src]);
  const accountName = useMemo(() => getQuickSightAccountName(src), [src]);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [framingBlocked, setFramingBlocked] = useState(false);

  // Detect CSP frame-ancestors blocking via iframe load event
  useEffect(() => {
    if (!iframeRef.current) return;

    const iframe = iframeRef.current;
    const handleLoad = () => {
      try {
        const contentDoc = iframe.contentDocument;
        if (contentDoc) {
          const bodyText = contentDoc.body?.innerText || '';
          if (bodyText.includes('refused to connect') || contentDoc.URL === 'chrome-error://chromewebdata/') {
            setFramingBlocked(true);
            logger.warn('QuickSight embedding blocked by CSP frame-ancestors', { src });
          }
        }
      } catch {
        // Cross-origin access denied — iframe loaded QuickSight content (not blocked)
      }
    };

    iframe.addEventListener('load', handleLoad);
    return () => iframe.removeEventListener('load', handleLoad);
  }, [src]);

  // Fallback: when auth times out, check if the iframe was CSP-blocked
  useEffect(() => {
    if (authState !== 'failed' || framingBlocked) return;

    const iframe = iframeRef.current;
    if (!iframe) {
      setFramingBlocked(true);
      return;
    }

    try {
      const doc = iframe.contentDocument;
      if (!doc) {
        return;
      }
      if (doc.URL === 'chrome-error://chromewebdata/' || doc.body?.innerText?.includes('refused to connect')) {
        setFramingBlocked(true);
        return;
      }
    } catch {
      return;
    }
  }, [authState, framingBlocked]);

  const showIframe = !framingBlocked && (authState === 'ready' || authState === 'loading');

  return (
    <div className={styles.iframeContainer} aria-label={title}>
      {!framingBlocked && authState === 'loading' && (
        <div className={styles.centeredStateContainer}>
          <LoadingSpinner />
        </div>
      )}

      {framingBlocked && (
        <div className={styles.centeredStateContainer}>
          <div className={styles.centeredStateContentWrapper}>
            <SpaceBetween size="m">
              <Alert type="warning" header="Embedding Not Available">
                This dashboard from the <strong>{accountName}</strong> QuickSight account cannot be embedded. The QuickSight administrator needs to
                allowlist FinSuite's domain under <strong>Manage QuickSight &rarr; Domains and Embedding</strong>.
              </Alert>
              <Box textAlign="center">
                <Link href={src} external variant="primary" fontSize="body-m">
                  Open Dashboard in New Tab
                </Link>
              </Box>
            </SpaceBetween>
          </div>
        </div>
      )}

      {!framingBlocked && authState === 'needs-auth' && (
        <div className={styles.centeredStateContainer}>
          <div className={styles.centeredStateContentWrapper}>
            <Alert type="info" action={<Button onClick={() => window.location.reload()}>Reload</Button>}>
              Please sign in to the{' '}
              <Link href={signInUrl} external>
                {accountName}
              </Link>{' '}
              QuickSight account first, then reload this page.
            </Alert>
          </div>
        </div>
      )}

      {!framingBlocked && authState === 'failed' && (
        <div className={styles.centeredStateContainer}>
          <div className={styles.centeredStateContentWrapper}>
            <Alert type="error">Authentication timed out. Please reload the page to try again.</Alert>
          </div>
        </div>
      )}

      <iframe
        ref={iframeRef}
        src={src}
        title={title}
        data-embed-app
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          border: 'none',
          display: showIframe ? 'block' : 'none'
        }}
        allow={`clipboard-read https://us-east-1.quicksight.aws.amazon.com https://us-west-2.quicksight.aws.amazon.com; clipboard-write https://us-east-1.quicksight.aws.amazon.com https://us-west-2.quicksight.aws.amazon.com`}
      />
    </div>
  );
};

export default QuickSightDashboardComponent;
